'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { useCategoryMegaMenu } from '@/providers/CategoryMegaMenuProvider';

import { Typography } from 'lighthouse';

import { CAR_BATTERY_VEHICLE_TYPE_ID, MOTOR_BATTERY_VEHICLE_TYPE_ID } from '@/constants/batteryProduct';
import { brandLanding } from '@/constants/routes';

import { getCategoryBrandsService } from '@/client/services/core/brand/methods';
import { CategoryMegaMenuItem, CategoryMegaMenuLink } from '@/types/categoryMegaMenu';
import { ArrowLeft2, CloseCircle, SearchNormal1 } from 'iconsax-react';

const MIN_SEARCH_LENGTH = 2;

const normalizeSearch = (value: string) => value.trim().toLowerCase();

const matchesQuery = (name: string, query: string) => {
    if (!query) return false;
    return name.toLowerCase().includes(query);
};

const categoryHasMatch = (category: CategoryMegaMenuItem, query: string) => {
    if (!query) return false;
    if (matchesQuery(category.name, query)) return true;

    return category.subCategories.some(
        (subCategory) => matchesQuery(subCategory.name, query) || subCategory.children.some((child) => matchesQuery(child.name, query))
    );
};

export const MobileServicesCatalog = () => {
    const router = useRouter();
    const inputRef = useRef<HTMLInputElement>(null);
    const categoryPanelRef = useRef<HTMLElement>(null);
    const contentPanelRef = useRef<HTMLElement>(null);
    const { categoryMegaMenuItems: categories, isCategoryMegaMenuLoaded } = useCategoryMegaMenu();
    const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [batteryBrands, setBatteryBrands] = useState<Record<number, CategoryMegaMenuLink[]>>({});
    const batteryCategoryIds = categories
        .filter((category) => category.slug === CAR_BATTERY_VEHICLE_TYPE_ID)
        .flatMap((category) => category.subCategories)
        .filter((category) => category.slug === CAR_BATTERY_VEHICLE_TYPE_ID || category.slug === MOTOR_BATTERY_VEHICLE_TYPE_ID)
        .map((category) => category.categoryId)
        .filter((categoryId): categoryId is number => categoryId !== undefined && categoryId > 0);
    const batteryCategoryIdsKey = [...new Set(batteryCategoryIds)].sort((a, b) => a - b).join(',');

    useEffect(() => {
        if (!batteryCategoryIdsKey || window.matchMedia('(min-width: 768px)').matches) {
            return;
        }

        let active = true;

        batteryCategoryIdsKey
            .split(',')
            .map(Number)
            .forEach((categoryId) => {
                const fetchBrands = async () => {
                    try {
                        const response = await getCategoryBrandsService({ categoryId, includeChildren: true, activeOnly: true });

                        if (active) {
                            setBatteryBrands((current) => ({
                                ...current,
                                [categoryId]: (response.data.content ?? []).map((brand) => ({
                                    id: brand.id,
                                    name: brand.name,
                                    slug: brand.slug,
                                    href: `${brandLanding}/${brand.slug}`
                                }))
                            }));
                        }
                    } catch {
                        if (active) {
                            setBatteryBrands((current) => ({ ...current, [categoryId]: [] }));
                        }
                    }
                };

                void fetchBrands();
            });

        return () => {
            active = false;
        };
    }, [batteryCategoryIdsKey]);

    const categoryMegaMenuItems = useMemo(
        () =>
            categories.map((category) =>
                category.slug === CAR_BATTERY_VEHICLE_TYPE_ID
                    ? {
                          ...category,
                          subCategories: category.subCategories.map((subCategory) => ({
                              ...subCategory,
                              children:
                                  subCategory.categoryId !== undefined
                                      ? (batteryBrands[subCategory.categoryId] ?? subCategory.children)
                                      : subCategory.children
                          }))
                      }
                    : category
            ),
        [categories, batteryBrands]
    );

    const normalizedQuery = useMemo(() => normalizeSearch(searchQuery), [searchQuery]);
    const activeSearchQuery = normalizedQuery.length >= MIN_SEARCH_LENGTH ? normalizedQuery : '';
    const hasInputValue = Boolean(normalizedQuery);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 768px)');

        const redirectDesktop = () => {
            if (mediaQuery.matches) {
                router.replace('/');
            }
        };

        redirectDesktop();
        mediaQuery.addEventListener('change', redirectDesktop);

        return () => mediaQuery.removeEventListener('change', redirectDesktop);
    }, [router]);

    useEffect(() => {
        if (!categoryMegaMenuItems.length) {
            setActiveCategoryId(null);
            return;
        }

        setActiveCategoryId((currentId) => {
            if (currentId && categoryMegaMenuItems.some((category) => category.id === currentId)) {
                return currentId;
            }

            return categoryMegaMenuItems[0].id;
        });
    }, [categoryMegaMenuItems]);

    useEffect(() => {
        if (!activeSearchQuery || !categoryMegaMenuItems.length) return;

        setActiveCategoryId((currentId) => {
            const currentHasMatch = categoryMegaMenuItems.some(
                (category) => category.id === currentId && categoryHasMatch(category, activeSearchQuery)
            );
            if (currentHasMatch) return currentId;

            const firstMatch = categoryMegaMenuItems.find((category) => categoryHasMatch(category, activeSearchQuery));
            return firstMatch?.id ?? currentId;
        });
    }, [activeSearchQuery, categoryMegaMenuItems]);

    useEffect(() => {
        if (!activeSearchQuery || !activeCategoryId) return;

        const scrollItemToTop = (container: HTMLElement | null, item: HTMLElement | null) => {
            if (!container || !item) return;
            const containerRect = container.getBoundingClientRect();
            const itemRect = item.getBoundingClientRect();
            container.scrollTo({
                top: container.scrollTop + (itemRect.top - containerRect.top),
                behavior: 'smooth'
            });
        };

        const categoryPanel = categoryPanelRef.current;
        const categoryItem = categoryPanel?.querySelector<HTMLElement>(`[data-category-id="${activeCategoryId}"]`) ?? null;
        scrollItemToTop(categoryPanel, categoryItem);

        const timeoutId = window.setTimeout(() => {
            const contentPanel = contentPanelRef.current;
            const matchedLink = contentPanel?.querySelector<HTMLElement>('[data-search-match="true"]') ?? null;
            scrollItemToTop(contentPanel, matchedLink);
        }, 50);

        return () => window.clearTimeout(timeoutId);
    }, [activeSearchQuery, activeCategoryId]);

    const activeCategory = categoryMegaMenuItems.find((category) => category.id === activeCategoryId) ?? categoryMegaMenuItems[0];
    const hasSubCategories = Boolean(activeCategory?.subCategories.length);

    const scrollBottomPad = 'pb-[calc(env(safe-area-inset-bottom)+5.5rem)]';

    return (
        <div className='flex h-[100dvh] flex-col overflow-hidden bg-white md:hidden'>
            <header className='shrink-0 border-0 border-b border-solid border-neutral-200 px-3 py-3'>
                <div className='relative flex min-h-11 items-center overflow-hidden rounded-lg border border-solid border-neutral-200 bg-neutral-50'>
                    <SearchNormal1 size={18} color='currentColor' className='pointer-events-none absolute right-3.5 text-neutral-400' />
                    <input
                        ref={inputRef}
                        type='text'
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder='جستجوی خدمات...'
                        className={`h-11 w-full border-0 bg-transparent py-0 pr-11 text-sm text-neutral-800 outline-none placeholder:text-neutral-400
                            ${hasInputValue ? 'pl-11' : 'pl-3.5'}`}
                        aria-label='جستجوی خدمات'
                        enterKeyHint='search'
                        autoComplete='off'
                    />
                    {hasInputValue ? (
                        <button
                            type='button'
                            aria-label='پاک کردن جستجو'
                            className='absolute left-2.5 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border-0
                                bg-transparent text-neutral-400'
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => {
                                setSearchQuery('');
                                inputRef.current?.focus();
                            }}>
                            <CloseCircle size={18} color='currentColor' variant='Bold' />
                        </button>
                    ) : null}
                </div>
            </header>

            {!isCategoryMegaMenuLoaded ? (
                <div className='grid min-h-0 flex-1 grid-cols-[110px_minmax(0,1fr)]'>
                    <div className={`animate-pulse space-y-0 border-0 border-l border-solid border-neutral-200 bg-neutral-50 ${scrollBottomPad}`}>
                        {Array.from({ length: 8 }).map((_, index) => (
                            <div key={index} className='flex h-14 items-center px-2'>
                                <div className='h-8 w-full rounded bg-neutral-200/80' />
                            </div>
                        ))}
                    </div>
                    <div className={`animate-pulse space-y-4 p-4 ${scrollBottomPad}`}>
                        {Array.from({ length: 4 }).map((_, index) => (
                            <div key={index} className='space-y-2'>
                                <div className='h-4 w-1/3 rounded bg-neutral-200/80' />
                                <div className='h-3 w-2/3 rounded bg-neutral-100' />
                                <div className='h-3 w-1/2 rounded bg-neutral-100' />
                            </div>
                        ))}
                    </div>
                </div>
            ) : !categoryMegaMenuItems.length ? (
                <div className={`flex flex-1 items-center justify-center px-6 ${scrollBottomPad}`}>
                    <Typography variant='body2' className='text-center text-neutral-500'>
                        خدمتی برای نمایش وجود ندارد
                    </Typography>
                </div>
            ) : (
                <div className='grid min-h-0 flex-1 grid-cols-[110px_minmax(0,1fr)]'>
                    <aside
                        ref={categoryPanelRef}
                        className={`nice-scroll min-h-0 overflow-y-auto border-0 border-l border-solid border-neutral-200 bg-neutral-50
                            ${scrollBottomPad}`}>
                        <ul className='m-0 list-none p-0'>
                            {categoryMegaMenuItems.map((category, index) => {
                                const isActive = category.id === activeCategory?.id;
                                const isLast = index === categoryMegaMenuItems.length - 1;
                                const isMatch = categoryHasMatch(category, activeSearchQuery);
                                const textClass = isActive || isMatch ? 'text-primary' : 'text-neutral-600';

                                return (
                                    <li
                                        key={category.id}
                                        data-category-id={category.id}
                                        className={`h-14 ${isLast ? '' : 'border-0 border-b border-solid border-neutral-200/60'}`}>
                                        <button
                                            type='button'
                                            onClick={() => setActiveCategoryId(category.id)}
                                            className={`all-unset relative flex h-full w-full cursor-pointer items-center justify-center px-2
                                                text-center transition-colors duration-150 ${isActive ? 'bg-white' : 'bg-transparent'} ${textClass}`}>
                                            <span
                                                className={`absolute inset-y-0 right-0 w-0.5 ${isActive ? 'bg-primary' : 'bg-transparent'}`}
                                                aria-hidden
                                            />
                                            <span className='line-clamp-2 w-full text-[11px] font-semibold leading-4'>{category.name}</span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </aside>

                    <section ref={contentPanelRef} className={`nice-scroll min-h-0 overflow-y-auto px-4 py-4 ${scrollBottomPad}`}>
                        <div className='mb-4 flex items-start justify-between gap-3'>
                            <div className='min-w-0'>
                                <Typography
                                    variant='subtitle2'
                                    className={`truncate text-sm font-bold ${
                                        matchesQuery(activeCategory.name, activeSearchQuery) ? 'text-primary' : 'text-neutral-900'
                                    }`}>
                                    {activeCategory.name}
                                </Typography>
                            </div>
                            {hasSubCategories ? (
                                <Link
                                    href={activeCategory.href}
                                    className='inline-flex shrink-0 items-center gap-1 text-[11px] font-bold text-primary no-underline'>
                                    همه
                                    <ArrowLeft2 size={12} color='currentColor' />
                                </Link>
                            ) : null}
                        </div>

                        {hasSubCategories ? (
                            <div className='flex flex-col gap-5'>
                                {activeCategory.subCategories.map((subCategory) => {
                                    const isSubMatch = matchesQuery(subCategory.name, activeSearchQuery);

                                    return (
                                        <div key={subCategory.id}>
                                            <Link
                                                href={subCategory.href}
                                                data-search-match={isSubMatch ? 'true' : undefined}
                                                className={`mb-2 block truncate text-sm font-bold no-underline active:text-primary ${
                                                    isSubMatch ? 'text-primary' : 'text-neutral-900'
                                                }`}>
                                                {subCategory.name}
                                            </Link>
                                            {subCategory.children.length ? (
                                                <ul className='m-0 flex list-none flex-col gap-1.5 p-0'>
                                                    {subCategory.children.map((child) => {
                                                        const isChildMatch = matchesQuery(child.name, activeSearchQuery);

                                                        return (
                                                            <li key={child.id}>
                                                                <Link
                                                                    href={child.href}
                                                                    data-search-match={isChildMatch ? 'true' : undefined}
                                                                    className={`block truncate text-xs leading-5 no-underline active:text-primary ${
                                                                        isChildMatch ? 'font-semibold text-primary' : 'text-neutral-500'
                                                                    }`}>
                                                                    {child.name}
                                                                </Link>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className='flex flex-col items-center justify-center py-10 text-center'>
                                <Link
                                    href={activeCategory.href}
                                    className='inline-flex items-center gap-1 text-sm font-bold text-primary no-underline'>
                                    مشاهده {activeCategory.name}
                                    <ArrowLeft2 size={14} color='currentColor' />
                                </Link>
                            </div>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
};
